import {Checkbox, TableBody, TableCell, TableRow} from '@mui/material'
import {useCallback, useMemo} from 'react'
import {useControlledFilterTableContainerProps} from './ControlledFilterTableContainer'

export const ControlledFilterTableBody = () => {
  const {columns, searchResults, idExtractor, selected, onSelectedChange, id} =
    useControlledFilterTableContainerProps<Record<string, any>>()

  const getRowCells = useCallback(
    (rowData: Record<string, any>, rowIndex: number) => {
      return columns.map((col) => {
        return (
          <TableCell
            id={id && `${id}-row-${rowIndex}-cell-${col.field}`}
            key={col.field}
            align={col.align}
          >
            {col.render ? col.render({data: rowData, rowIndex}) : rowData[col.field]}
          </TableCell>
        )
      })
    },
    [columns, id]
  )

  const handleSelectChange = useCallback(
    (row: Record<string, any>) => () => {
      if (selected && onSelectedChange) {
        const id = idExtractor(row)
        const newSelected = [...selected]
        const selectedIndex = newSelected.findIndex((item) => idExtractor(item) === id)
        if (selectedIndex >= 0) {
          newSelected.splice(selectedIndex, 1)
        } else {
          newSelected.push(row)
        }
        onSelectedChange(newSelected)
      }
    },
    [idExtractor, onSelectedChange, selected]
  )

  const getCheckboxCell = useCallback(
    (row: Record<string, any>, isSelected: boolean, rowIndex: number) => {
      if (selected) {
        return (
          <TableCell id={id && `${id}-row-${rowIndex}-checkbox-cell`}>
            <Checkbox
              id={id && `${id}-row-${rowIndex}-checkbox`}
              size='small'
              checked={isSelected}
              onChange={handleSelectChange(row)}
            />
          </TableCell>
        )
      }
    },
    [handleSelectChange, id, selected]
  )

  const isRowSelected = useCallback(
    (rowData: Record<string, any>) => {
      const id = idExtractor(rowData)
      return selected?.some((item) => idExtractor(item) === id) || false
    },
    [idExtractor, selected]
  )

  const rows = useMemo(() => {
    return searchResults?.data.map((rowData, rowIndex) => {
      const dataId = idExtractor(rowData)
      const isSelected = isRowSelected(rowData)
      return (
        <TableRow
          id={id && `${id}-row-${rowIndex}`}
          key={dataId}
          sx={{'&:last-child td, &:last-child th': {border: 0}}}
        >
          {getCheckboxCell(rowData, isSelected, rowIndex)}
          {getRowCells(rowData, rowIndex)}
        </TableRow>
      )
    })
  }, [getCheckboxCell, getRowCells, id, idExtractor, isRowSelected, searchResults?.data])

  return <TableBody>{rows}</TableBody>
}
